import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { useFinalizeAccountsMapping } from "@api/accountingIntegration.api";
import { ChartOfAccountsOrderMode } from "@common/helpers/domains/accounting/buildHierarchicalChartOfAccounts";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { AccountingMigrationResponse } from "@common/schemas/accounting-integration.schema";
import {
    Account,
    AccountGroup,
} from "@common/types/domains/accounting/account";
import { buildHierarchicalChartOfAccounts } from "@components/accounting/chartOfAccounts/utils";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { getImportedAccountProvider } from "../../importedAccountProvider";
import { RefinalizeMappingConfirmationModal } from "../../RefinalizeMappingConfirmationModal";
import { useNavigateAfterMappingFinalized } from "../../useNavigateAfterMappingFinalized";
import { CoaMappingRow } from "../coaMappingRowTypes";
import { CoaMappingViewConfig } from "../coaMappingViewConfig";
import { buildCoaMappingPreview } from "./buildCoaMappingPreview";
import { CoaMappingPreviewIsland } from "./CoaMappingPreviewIsland";
import {
    CoaMappingPreviewMappedFilterCombobox,
    MappedFilter,
} from "./CoaMappingPreviewMappedFilterCombobox";
import { CoaMappingPreviewSourcesSidebar } from "./CoaMappingPreviewSourcesSidebar";
import { CoaMappingPreviewSourcesToggle } from "./CoaMappingPreviewSourcesToggle";
import { CoaMappingPreviewTableMeta } from "./coaMappingPreviewTableMeta";
import {
    buildCoaMappingPreviewView,
    createCoaMappingPreviewViewConfig,
} from "./coaMappingPreviewViewConfig";
import { findFirstMappedAccountId } from "./findFirstMappedAccountId";
import {
    PREVIEW_TABLE_PANEL_ID,
    SOURCES_SIDEBAR_PANEL_ID,
    useCoaMappingPreviewSourcesSidebar,
} from "./useCoaMappingPreviewSourcesSidebar";
import drawerStyles from "../CoaMappingDrawer.module.scss";
import styles from "./CoaMappingPreviewStep.module.scss";

const view = buildCoaMappingPreviewView();

interface Props {
    entityId: number;
    nextTaskId: string | undefined;
    accountingMigration: AccountingMigrationResponse | null | undefined;
    importedAccounts: ImportedAccount[];
    sourceRows: CoaMappingRow[] | null;
    sourceViewConfig: CoaMappingViewConfig;
    kickAccounts: Account[];
    kickGroups: AccountGroup[];
    onBack: () => void;
    escapeInterceptorRef: MutableRefObject<(() => void) | null>;
}

export const CoaMappingPreviewStep = ({
    entityId,
    nextTaskId,
    accountingMigration,
    importedAccounts,
    sourceRows,
    sourceViewConfig,
    kickAccounts,
    kickGroups,
    onBack,
    escapeInterceptorRef,
}: Props) => {
    const showAccountNumbers = useShowAccountNumbers();
    const [search, setSearch] = useState<string | undefined>();
    const [mappedFilter, setMappedFilter] = useState<MappedFilter>("all");
    const [isRefinalizeModalOpen, setIsRefinalizeModalOpen] = useState(false);

    const preview = useMemo(
        () =>
            buildCoaMappingPreview({
                importedAccounts,
                kickAccounts,
                entityId,
            }),
        [importedAccounts, kickAccounts, entityId],
    );
    const filteredAccounts = useMemo(
        () =>
            mappedFilter === "all"
                ? preview.accounts
                : preview.accounts.filter(
                      (account) =>
                          preview.sourcesByAccountId.has(account.id) ===
                          (mappedFilter === "mapped"),
                  ),
        [preview, mappedFilter],
    );
    const rows = useMemo(
        () =>
            buildHierarchicalChartOfAccounts({
                accounts: filteredAccounts,
                groups: kickGroups,
                filters: { search },
                orderMode: ChartOfAccountsOrderMode.sortOrder,
                showAccountNumbers,
                withPredefinedSections: true,
                hasExternalFilterActive: mappedFilter !== "all",
            }),
        [
            filteredAccounts,
            kickGroups,
            search,
            showAccountNumbers,
            mappedFilter,
        ],
    );
    const firstMappedAccountId = useMemo(
        () =>
            findFirstMappedAccountId({
                rows,
                sourcesByAccountId: preview.sourcesByAccountId,
            }),
        [rows, preview],
    );
    const sourcesSidebar = useCoaMappingPreviewSourcesSidebar({
        preview,
        fallbackAccountId: firstMappedAccountId,
        escapeInterceptorRef,
    });
    const viewConfig = useMemo(
        () =>
            createCoaMappingPreviewViewConfig({
                providerLabel: getImportedAccountProvider(
                    accountingMigration?.externalSystem,
                ).label,
            }),
        [accountingMigration?.externalSystem],
    );
    const { viewSources } = sourcesSidebar;
    const tableMeta = useMemo<CoaMappingPreviewTableMeta>(
        () => ({
            callbacks: { onViewMappedSources: viewSources },
            options: {
                sourcesByAccountId: preview.sourcesByAccountId,
                newAccountIds: preview.newAccountIds,
                showAccountNumbers,
            },
        }),
        [preview, showAccountNumbers, viewSources],
    );

    const finalizeMutation = useFinalizeAccountsMapping(entityId);
    const onFinalized = useNavigateAfterMappingFinalized({ nextTaskId });
    const isMappingFinalized =
        !!accountingMigration?.accountsMappingFinalizedAt;
    const onConfirm = useCallback(() => {
        if (isMappingFinalized) {
            setIsRefinalizeModalOpen(true);
            return;
        }
        finalizeMutation.mutate({}, { onSuccess: onFinalized });
    }, [isMappingFinalized, finalizeMutation, onFinalized]);
    const onConfirmRefinalization = useCallback(async () => {
        await finalizeMutation
            .mutateAsync({ isRefinalization: true })
            .finally(() => {
                setIsRefinalizeModalOpen(false);
            });
        onFinalized();
    }, [finalizeMutation, onFinalized]);

    return (
        <>
            <TablePageHeader
                isFullPage
                borderBottom
                search={search}
                onSearchChange={(value) => setSearch(value || undefined)}
                searchKeyboardShortcuts={false}
            >
                <CoaMappingPreviewMappedFilterCombobox
                    value={mappedFilter}
                    onChange={setMappedFilter}
                />
                <CoaMappingPreviewSourcesToggle
                    isShown={sourcesSidebar.isShown}
                    isDisabled={!sourcesSidebar.canShow}
                    onToggle={sourcesSidebar.toggle}
                />
            </TablePageHeader>
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={sourcesSidebar.initialLayout}
                onLayoutChanged={sourcesSidebar.onLayoutChanged}
                className={styles.split}
            >
                <ResizablePanel id={PREVIEW_TABLE_PANEL_ID} minSize="50%">
                    <div className={drawerStyles.pane}>
                        <div className={drawerStyles.tableContainer}>
                            <ViewTable
                                data={rows}
                                view={view}
                                config={viewConfig}
                                tableMeta={tableMeta}
                                autoExpandOnMount
                                nestingIndentPx={16}
                                rowBorders={false}
                                enableColumnResizing
                                emptyView={() => (
                                    <EmptyState header="No accounts for the selected filters" />
                                )}
                                footer={
                                    <CoaMappingPreviewIsland
                                        totalAccountCount={
                                            preview.totalAccountCount
                                        }
                                        confirmLabel={
                                            isMappingFinalized
                                                ? "Update mapping"
                                                : "Confirm"
                                        }
                                        isConfirming={
                                            finalizeMutation.isPending ||
                                            finalizeMutation.isSuccess
                                        }
                                        onBack={onBack}
                                        onConfirm={onConfirm}
                                    />
                                }
                            />
                        </div>
                    </div>
                </ResizablePanel>
                <ResizableHandle disabled={!sourcesSidebar.canShow} />
                <ResizablePanel
                    id={SOURCES_SIDEBAR_PANEL_ID}
                    minSize={280}
                    maxSize="50%"
                    collapsible
                    panelRef={sourcesSidebar.panelRef}
                    onResize={sourcesSidebar.onSidebarResize}
                >
                    {sourcesSidebar.isShown && sourcesSidebar.account && (
                        <CoaMappingPreviewSourcesSidebar
                            account={sourcesSidebar.account}
                            preview={preview}
                            sourceRows={sourceRows}
                            sourceViewConfig={sourceViewConfig}
                            showAccountNumbers={showAccountNumbers}
                        />
                    )}
                </ResizablePanel>
            </ResizablePanelGroup>
            <RefinalizeMappingConfirmationModal
                show={isRefinalizeModalOpen}
                onConfirm={onConfirmRefinalization}
                onClose={() => setIsRefinalizeModalOpen(false)}
            />
        </>
    );
};
